import requestWrapper from '../../requestWrapper';
import endpoints from './endpoints';
import type {ContentType} from '@deezer/next/services/types/contentType';
import type {HighlightResponse} from '@deezer/next/services/types/highlights';
import type {DataResponseWrapper} from '@deezer/next/services/api/Starmania/requests/types';
import type {
	ArtistBiography,
	ArtistPerformances,
	ArtistTop,
	EditArtist,
} from './types';
import type {
	AnalyticFilters,
	AnalyticsFavorites,
	AnalyticsDemographics,
	AnalyticsMap,
	AnalyticsStreamingContext,
} from '../types';

const {post, patch, get} = requestWrapper;

const requests = {
	getPerformances: (artistId: string, filters: AnalyticFilters) =>
		get<DataResponseWrapper<ArtistPerformances>>(
			endpoints.performances(artistId),
			{
				params: filters,
			},
		),
	getTop: (artistId: string, filters: AnalyticFilters) =>
		get<DataResponseWrapper<ArtistTop>>(endpoints.top(artistId), {
			params: filters,
		}),
	getFavorites: (artistId: string, filters: AnalyticFilters) =>
		get<DataResponseWrapper<AnalyticsFavorites>>(
			endpoints.favorites(artistId),
			{
				params: filters,
			},
		),
	getMap: (artistId: string, filters: AnalyticFilters) =>
		get<DataResponseWrapper<AnalyticsMap>>(endpoints.map(artistId), {
			params: filters,
		}),
	getDemographics: (artistId: string, filters: AnalyticFilters) =>
		get<DataResponseWrapper<AnalyticsDemographics>>(
			endpoints.demographics(artistId),
			{
				params: filters,
			},
		),
	getStreamingContext: (artistId: string, filters: AnalyticFilters) =>
		get<DataResponseWrapper<AnalyticsStreamingContext>>(
			endpoints.streamingContext(artistId),
			{
				params: filters,
			},
		),
	createHighlight: (
		artistId: string,
		data: {
			dateStartDisplay: string;
			contentId: string;
			countries: string;
			contentType: ContentType;
			dateEndDisplay: string;
		},
	) => {
		return post<HighlightResponse>(
			endpoints.createArtistHighlight(artistId),
			data,
		);
	},
	editHighlight: (
		artistId: string,
		highlightId: string,
		data: {
			dateStartDisplay: string;
			countries: string;
			dateEndDisplay: string;
		},
	) => {
		return patch<HighlightResponse>(
			endpoints.editArtistHighlight(artistId, highlightId),
			data,
		);
	},
	deleteHighlight: (artistId: string, highlightId: string) => {
		return requestWrapper.delete<HighlightResponse>(
			endpoints.editArtistHighlight(artistId, highlightId),
		);
	},
	getArtist: (artistId: string, lang: string) =>
		get<DataResponseWrapper<EditArtist>>(endpoints.artist(artistId), {
			params: {lang},
		}),
	getArtistStatus: async (artistId: string) => {
		const artist = await get<DataResponseWrapper<EditArtist>>(
			endpoints.artist(artistId),
			{
				params: {lang: 'en'},
			},
		);
		return artist.data.status;
	},
	getArtistBiography: (artistId: string, lang = 'en') =>
		get<DataResponseWrapper<ArtistBiography>>(
			endpoints.artistBiography(artistId),
			{
				params: {lang},
			},
		),
	patchArtist: (artistId: string, data: any) =>
		patch<DataResponseWrapper<{title: string}>>(
			endpoints.artist(artistId),
			data,
		),
	patchArtistStatus: (artistId: string, status?: string) =>
		patch<DataResponseWrapper<{title: string}>>(endpoints.artist(artistId), {
			status,
		}),
	uploadPicture: (artistId: string, picture: any) => {
		const body = new FormData();
		body.append('file', picture);
		return post<DataResponseWrapper<{title: string}>>(
			endpoints.artistPicture(artistId),
			body,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
					'x-requested-with': 'xmlhttprequest',
				},
			},
		);
	},
	deletePicture: (artistId: string) => {
		return requestWrapper.delete<DataResponseWrapper<{title: string}>>(
			endpoints.artistPicture(artistId),
		);
	},
};

export default requests;
